'use client'

import { useRef, useState } from 'react'
import {
  Button,
  Col,
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  Icon,
  Row,
  Alert,
} from '@repo/ui/components'
import { useTranslate } from '@repo/shared/hooks'
import Image from 'next/image'
import { EditProfileCoverModalProps } from './edit-profile.types'

import { globalConfig } from '@repo/shared/utils'
import { useAgentUploadCoverPicture } from '@repo/services'
import { useErrorHandler } from '@repo/shared/hooks'
import { UploadedImage } from '@/components'

import { ImageCropper } from '@/components/image-cropper'

export const EditProfileCoverModal = ({
  open,
  onOpenChange,
  coverImage,
}: EditProfileCoverModalProps) => {
  const { t } = useTranslate()
  const { errorHandler } = useErrorHandler()

  const {
    mutate: uploadCoverPhotoMutation,
    isPending: isUploadCoverPhotoPending,
  } = useAgentUploadCoverPicture({
    invalidate: true,
  })

  const coverFileInputRef = useRef<HTMLInputElement>(null)

  const [uploadedCoverImage, setUploadedCoverImage] = useState<
    UploadedImage | undefined
  >(
    coverImage
      ? {
          id: 0,
          url: `${globalConfig.baseImageUrl}${coverImage}`,
          file: null,
          name: '',
          size: 0,
          status: 'success',
        }
      : undefined,
  )

  const [cropperCoverImage, setCropperCoverImage] = useState<string | null>(
    null,
  )
  const [isCropperCoverOpen, setIsCropperCoverOpen] = useState(false)

  const handleCoverProfileImageUpload = (
    file: File,
    callback: (success: boolean) => void,
  ) => {
    const formData = new FormData()
    formData.append('file', file)
    uploadCoverPhotoMutation(formData, {
      onSuccess: () => {
        callback(true)
      },
      onError: (error) => {
        errorHandler(error, {
          type: 'both',
        })
      },
    })
  }

  const handleSubmit = () => {
    if (
      uploadedCoverImage &&
      uploadedCoverImage.file &&
      uploadedCoverImage.status === 'pending'
    ) {
      handleCoverProfileImageUpload(uploadedCoverImage.file!, (success) => {
        if (success) {
          onOpenChange(false)
        }
      })
    }
  }

  const handleCoverImageUpload = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0]
    if (file) {
      const imageUrl = URL.createObjectURL(file)
      setCropperCoverImage(imageUrl)
      setIsCropperCoverOpen(true)
    }
    // Reset input value to allow selecting the same file again
  }

  // const handleCoverImageDelete = () => {
  //   setUploadedCoverImage(undefined)
  // }

  const handleNewCoverImageClick = () => {
    coverFileInputRef.current?.click()
  }

  const handleCropCoverComplete = (croppedFile: File) => {
    setUploadedCoverImage({
      id: Number(Math.random().toString(10).substring(2, 15)),
      url: URL.createObjectURL(croppedFile),
      file: croppedFile,
      name: croppedFile.name,
      size: croppedFile.size,
      status: 'pending',
    })
    setCropperCoverImage(null)
    setIsCropperCoverOpen(false)
  }

  const handleCropperCoverClose = () => {
    setCropperCoverImage(null)
    setIsCropperCoverOpen(false)
  }

  const currentCoverImage = uploadedCoverImage
    ? uploadedCoverImage.url
    : '/images/cover-image.png'

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent>
        <ModalHeader>
          <ModalTitle className='text-center'>
            {t('editProfile_cover_title')}
          </ModalTitle>
        </ModalHeader>

        <Col className='p-lg max-h-[600px] overflow-y-auto'>
          {/* Cover Image Section */}
          <Row className='items-center gap-md mb-lg'>
            <Row className='gap-xs'>
              <Button
                type='button'
                variant='fill'
                size='sm'
                color='base'
                startIcon={<Icon name='Plus' size='sm' />}
                onClick={handleNewCoverImageClick}
              >
                {t('editProfile_newCoverImage')}
              </Button>
              {/* {uploadedCoverImage && (
                <Button
                  type='button'
                  variant='ghost'
                  size='sm'
                  color='negative'
                  startIcon={<Icon name='TrashBinLine' size='sm' />}
                  onClick={handleCoverImageDelete}
                  className='border border-negative-surface-100 text-negative-surface-100 hover:bg-negative-surface-20'
                >
                  {t('editProfile_deleteCoverImage')}
                </Button>
              )} */}
              <input
                ref={coverFileInputRef}
                type='file'
                accept='image/*'
                onChange={handleCoverImageUpload}
                className='hidden'
              />
            </Row>
          </Row>

          <Row className='items-center gap-md mb-lg justify-center'>
            <div className='rounded-2xs bg-neutral-surface-20 overflow-hidden'>
              <Image
                src={currentCoverImage}
                width={256}
                height={64}
                alt='Cover'
                className='object-cover'
              />
            </div>
          </Row>

          <Alert
            color='alert'
            caption={t('editProfile_warning_message')}
            icon={<Icon name='AlertTriangle' />}
          />

          {/* Submit Button */}
          <Button
            type='submit'
            variant='fill'
            size='lg'
            color='base'
            width='full'
            className='mt-lg'
            loading={isUploadCoverPhotoPending}
            onClick={() => handleSubmit()}
          >
            {t('editProfile_saveChanges')}
          </Button>
        </Col>
      </ModalContent>

      <ImageCropper
        open={isCropperCoverOpen}
        onClose={handleCropperCoverClose}
        imageSrc={cropperCoverImage}
        onCropComplete={handleCropCoverComplete}
        aspect={16 / 9}
        cropShape='rect'
      />
    </Modal>
  )
}
